import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react"

import { cn } from "@coval/ui/cn"

import type {
  VisualizerBucketAssignment,
  VisualizerConversation,
} from "./uploadedConversationsVisualizerDataset"
import type { PlaceholderBucket } from "./uploadedConversationsVisualizerPlaceholderLenses"

const FALLBACK_ANOMALY_MARKER_COLOR = "var(--coval-chart-series-1)"
const ANOMALIES_OVERFLOW_EPSILON_PX = 1

interface UploadedConversationsVisualizerAnomaliesQuestionsProps {
  bucketAssignments: VisualizerBucketAssignment[]
  buckets: PlaceholderBucket[]
  markerPalette: string[]
  onActiveMarkerColorChange?: (markerColor: string | null) => void
  onHighlightedConversationIdsChange?: (
    conversationIds: ReadonlySet<string> | null,
  ) => void
  seedKey: string
}

export function UploadedConversationsVisualizerAnomaliesQuestions({
  bucketAssignments,
  buckets,
  markerPalette,
  onActiveMarkerColorChange,
  onHighlightedConversationIdsChange,
  seedKey,
}: UploadedConversationsVisualizerAnomaliesQuestionsProps) {
  const viewportRef = useRef<HTMLDivElement>(null)
  const anomaliesListRef = useRef<HTMLUListElement>(null)
  const [activeBucketKey, setActiveBucketKey] = useState<string | null>(
    buckets[0]?.key ?? null,
  )
  const [hasOverflow, setHasOverflow] = useState(false)

  const syncOverflowState = useCallback(() => {
    const viewport = viewportRef.current

    if (!viewport) {
      return
    }

    const maxScrollTop = Math.max(
      0,
      viewport.scrollHeight - viewport.clientHeight,
    )
    const nextHasOverflow = maxScrollTop > ANOMALIES_OVERFLOW_EPSILON_PX

    setHasOverflow((currentHasOverflow) =>
      currentHasOverflow === nextHasOverflow
        ? currentHasOverflow
        : nextHasOverflow,
    )
  }, [])

  useLayoutEffect(() => {
    const viewport = viewportRef.current
    const anomaliesList = anomaliesListRef.current

    if (!viewport || !anomaliesList) {
      return
    }

    let animationFrame = 0

    const queueSync = () => {
      window.cancelAnimationFrame(animationFrame)
      animationFrame = window.requestAnimationFrame(syncOverflowState)
    }

    queueSync()

    const resizeObserver =
      typeof ResizeObserver === "undefined"
        ? null
        : new ResizeObserver(() => {
            queueSync()
          })

    resizeObserver?.observe(viewport)
    resizeObserver?.observe(anomaliesList)

    return () => {
      window.cancelAnimationFrame(animationFrame)
      resizeObserver?.disconnect()
    }
  }, [activeBucketKey, buckets, syncOverflowState])

  useEffect(() => {
    setActiveBucketKey(buckets[0]?.key ?? null)
  }, [buckets, seedKey])

  useEffect(() => {
    if (!activeBucketKey) {
      onActiveMarkerColorChange?.(null)
      onHighlightedConversationIdsChange?.(null)
      return
    }

    const activeBucketIndex = buckets.findIndex(
      (bucket) => bucket.key === activeBucketKey,
    )

    if (activeBucketIndex < 0) {
      onActiveMarkerColorChange?.(null)
      onHighlightedConversationIdsChange?.(null)
      return
    }

    const activeAssignment = bucketAssignments.find(
      (bucketAssignment) => bucketAssignment.bucketKey === activeBucketKey,
    )

    onActiveMarkerColorChange?.(
      markerPalette[activeBucketIndex] ?? FALLBACK_ANOMALY_MARKER_COLOR,
    )
    onHighlightedConversationIdsChange?.(
      new Set(
        (activeAssignment?.conversations ?? []).map(
          (conversation: VisualizerConversation) => conversation.id,
        ),
      ),
    )
  }, [
    activeBucketKey,
    bucketAssignments,
    buckets,
    markerPalette,
    onActiveMarkerColorChange,
    onHighlightedConversationIdsChange,
  ])

  useEffect(() => {
    return () => {
      onActiveMarkerColorChange?.(null)
      onHighlightedConversationIdsChange?.(null)
    }
  }, [onActiveMarkerColorChange, onHighlightedConversationIdsChange])

  return (
    <div className="flex h-full min-h-0 min-w-0 self-stretch p-1">
      <div
        ref={viewportRef}
        className="flex h-full w-full min-h-0 flex-col overflow-x-hidden overflow-y-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        <ul
          ref={anomaliesListRef}
          className={cn(
            "w-full space-y-1.5 py-1",
            hasOverflow ? "my-0" : "my-auto",
          )}
          data-testid="uploaded-conversations-anomalies-questions"
        >
          {buckets.map((bucket, bucketIndex) => {
            const isActive = bucket.key === activeBucketKey
            const markerColor =
              markerPalette[bucketIndex] ?? FALLBACK_ANOMALY_MARKER_COLOR
            return (
              <li key={bucket.key} className="overflow-hidden px-0.5 py-1">
                <div className="flex items-start gap-2">
                  <span
                    aria-hidden="true"
                    className="mt-1 block size-2 shrink-0 rounded-full ring-1 ring-inset ring-coval-border"
                    style={{ backgroundColor: markerColor }}
                  />
                  <div className="min-w-0">
                    <button
                      type="button"
                      className={cn(
                        "w-full text-left text-sm leading-5 transition-[color,transform,font-weight] duration-200 ease-out motion-reduce:transition-none",
                        "focus-visible:outline-none",
                        isActive
                          ? "scale-[1.01] font-medium text-coval-text"
                          : "font-normal text-coval-text-muted",
                      )}
                      onClick={() => {
                        setActiveBucketKey((currentBucketKey) =>
                          currentBucketKey === bucket.key ? null : bucket.key,
                        )
                      }}
                      aria-expanded={isActive}
                      data-testid={`uploaded-conversations-anomalies-question-${bucket.key}`}
                    >
                      {bucket.label}
                    </button>
                    <div
                      className={cn(
                        "grid overflow-hidden transition-[grid-template-rows,opacity,margin] duration-200 ease-out motion-reduce:transition-none",
                        isActive
                          ? "mt-1 grid-rows-[1fr] opacity-100"
                          : "grid-rows-[0fr] opacity-0",
                      )}
                      data-testid={`uploaded-conversations-anomalies-question-description-${bucket.key}`}
                    >
                      <p className="min-h-0 overflow-hidden text-xs leading-4 text-coval-text-muted">
                        {bucket.description}
                      </p>
                    </div>
                  </div>
                </div>
              </li>
            )
          })}
        </ul>
      </div>
    </div>
  )
}
